/**
 * PhaseExecute — Dashboard de Fase: anéis ao vivo + roadmap + logging shortcuts.
 * Route: /programs/:program/phase
 *
 * Reads GET /api/programs/{program}/phase
 * Logging endpoints: /log/nutrition, /log/training, /log/hygiene, /log/biomarker
 * Gate: POST /attempt-gate
 */

import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, Trophy, X, Check, Lock } from "lucide-react";
import { toast } from "sonner";

import RingsLive from "@/components/progression/RingsLive";
import PhaseRoadmap from "@/components/progression/PhaseRoadmap";
import SpiritWeekGuideCard from "@/components/progression/SpiritWeekGuideCard";
import FusionMacroBoard from "@/components/progression/FusionMacroBoard";
import RefeedTimer from "@/components/progression/RefeedTimer";
import MealPrescriptionModal from "@/components/progression/MealPrescriptionModal";
import WeeklyMacroCalendar from "@/components/progression/WeeklyMacroCalendar";
import TrainingScheduleSelector from "@/components/progression/TrainingScheduleSelector";
import TdeeCalibrationBadge from "@/components/progression/TdeeCalibrationBadge";
import WeeklyLessonCard from "@/components/progression/WeeklyLessonCard";
import RoadmapPreviewStrip from "@/components/progression/RoadmapPreviewStrip";
import FusionGoalWizard from "@/components/progression/FusionGoalWizard";
import SpiritNudgeCard from "@/components/progression/SpiritNudgeCard";
import DailyFocusCard from "@/components/progression/DailyFocusCard";
import SessionLoggerModal from "@/components/progression/SessionLoggerModal";
import CloseDayModal from "@/components/progression/CloseDayModal";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const RING_LABEL_PT = {
  nutrition: "Nutrição",
  training: "Treino",
  hygiene: "Higiene",
  biomarkers: "Biomarcadores",
};

// ---------------------------------------------------------------------------
// Logging modals — minimalist, one per ring
// ---------------------------------------------------------------------------

// --- Ring metadata used across modal + tiles (single source) ---
const RING_META_UI = {
  nutrition: {
    label: "Nutrição",
    hint: "Como comeste hoje?",
    color: "#84cc16",
    cadence_pt: "Diário",
  },
  training: {
    label: "Treino",
    hint: "Sessão de hoje",
    color: "#a855f7",
    cadence_pt: "Por sessão",
  },
  hygiene: {
    label: "Higiene",
    hint: "Sono · água · sais",
    color: "#06b6d4",
    cadence_pt: "Diário",
  },
  biomarkers: {
    label: "Biomarcadores",
    hint: "Peso e cintura",
    color: "#00ff88",
    cadence_pt: "Semanal",
  },
};

// --- Quick presets — one-tap logging (huge friction reducer) ---
const NUTRITION_PRESETS = [
  { key: "perfect", label: "Perfeito", sub: "<20g carbs", color: "#84cc16",
    values: { carbs_g: 15, protein_g: 140, fat_g: 120, kcal: 1800 } },
  { key: "light", label: "Ligeiro", sub: "20-40g", color: "#facc15",
    values: { carbs_g: 30, protein_g: 130, fat_g: 100, kcal: 1700 } },
  { key: "off", label: "Falhei", sub: ">40g", color: "#f97316",
    values: { carbs_g: 60, protein_g: 110, fat_g: 80, kcal: 1600 } },
];

function LogModal({ open, ring, onClose, onSubmit }) {
  const [values, setValues] = useState({});
  const [preset, setPreset] = useState(null);
  const [toggles, setToggles] = useState({ sleep_ok: false, water_ok: false, electrolytes: false });

  useEffect(() => {
    setValues({});
    setPreset(null);
    setToggles({ sleep_ok: false, water_ok: false, electrolytes: false });
  }, [open, ring]);

  if (!open || !ring) return null;

  const meta = RING_META_UI[ring] || { label: RING_LABEL_PT[ring] || ring, color: "#84cc16", cadence_pt: "" };

  // Simplified field sets — biomarkers reduced to essentials (weekly)
  const fields = {
    nutrition: [
      { key: "carbs_g", label: "Carbs (g)", placeholder: "15" },
      { key: "protein_g", label: "Proteína (g)", placeholder: "140" },
      { key: "fat_g", label: "Gordura (g)", placeholder: "100" },
      { key: "kcal", label: "Kcal", placeholder: "1800" },
    ],
    hygiene: [
      { key: "mood", label: "Mood (1-10)", placeholder: "8" },
    ],
    biomarkers: [
      { key: "weight", label: "Peso (kg)", placeholder: "78.5" },
      { key: "waist_cm", label: "Cintura (cm)", placeholder: "82" },
    ],
  }[ring] || [];

  const handlePreset = (p) => {
    setPreset(p.key);
    setValues(p.values);
  };

  const handleSubmit = () => {
    const payload = {};

    if (ring === "hygiene") {
      // Toggle-driven — infer numeric values that the backend expects
      payload.sleep_h = toggles.sleep_ok ? 7.5 : 6;
      payload.water_l = toggles.water_ok ? 2.5 : 1.5;
      payload.electrolytes = !!toggles.electrolytes;
      const mood = values.mood;
      if (mood !== undefined && mood !== "") payload.mood = parseFloat(mood);
    } else {
      fields.forEach((f) => {
        const v = values[f.key];
        if (v !== undefined && v !== "") {
          payload[f.key] = parseFloat(v);
        }
      });
    }
    onSubmit(ring, payload);
  };

  // Bottom-sheet on mobile · centered on desktop
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid={`log-modal-${ring}`}
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 26, stiffness: 260 }}
          className="w-full sm:max-w-md relative rounded-t-3xl sm:rounded-3xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: `linear-gradient(180deg, ${meta.color}08 0%, #09090b 40%)`,
            border: `1px solid ${meta.color}30`,
            boxShadow: `0 -20px 60px ${meta.color}25, inset 0 1px 0 ${meta.color}30`,
          }}
        >
          {/* Grip handle for mobile */}
          <div className="sm:hidden flex justify-center pt-3 pb-1">
            <div className="w-12 h-1 rounded-full bg-zinc-700" />
          </div>

          <div className="px-6 pt-5 pb-6">
            <div className="flex items-start justify-between mb-1">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: meta.color }}>
                  Registar · {meta.cadence_pt}
                </p>
                <h3 className="text-2xl font-black text-zinc-100 leading-tight mt-0.5">{meta.label}</h3>
                <p className="text-xs text-zinc-500 mt-1">{meta.hint}</p>
              </div>
              <button
                onClick={onClose}
                className="text-zinc-400 hover:text-zinc-100 p-1 -mt-1 -mr-1"
                data-testid="log-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Weekly nudge */}
            {ring === "biomarkers" && (
              <div className="mt-3 mb-4 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/25">
                <p className="text-[11px] text-emerald-300 leading-relaxed">
                  ✨ <span className="font-bold">Só precisas 1x por semana.</span> Sem fricção, sem obsessão.
                </p>
              </div>
            )}

            {/* Nutrition presets — one-tap logging */}
            {ring === "nutrition" && (
              <div className="mt-4 mb-3">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Preset rápido</p>
                <div className="grid grid-cols-3 gap-2">
                  {NUTRITION_PRESETS.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => handlePreset(p)}
                      className="p-3 rounded-xl border text-left transition-all active:scale-95"
                      style={{
                        borderColor: preset === p.key ? p.color : "#27272a",
                        background: preset === p.key ? `${p.color}20` : "rgba(24,24,27,0.7)",
                        boxShadow: preset === p.key ? `0 0 20px ${p.color}40` : "none",
                      }}
                      data-testid={`preset-nutrition-${p.key}`}
                    >
                      <div className="text-sm font-bold" style={{ color: p.color }}>{p.label}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{p.sub}</div>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-600 mt-2 italic">Ou ajusta manualmente ↓</p>
              </div>
            )}

            {/* Hygiene toggles — check-in style (near-zero friction) */}
            {ring === "hygiene" && (
              <div className="mt-4 space-y-2">
                {[
                  { key: "sleep_ok", label: "Dormiste ≥7h", emoji: "😴" },
                  { key: "water_ok", label: "Bebeste ≥2.5L água", emoji: "💧" },
                  { key: "electrolytes", label: "Tomaste electrólitos", emoji: "⚡" },
                ].map((t) => {
                  const on = !!toggles[t.key];
                  return (
                    <button
                      key={t.key}
                      onClick={() => setToggles({ ...toggles, [t.key]: !on })}
                      className="w-full flex items-center gap-3 p-3 rounded-xl border transition-all active:scale-[0.98]"
                      style={{
                        borderColor: on ? meta.color : "#27272a",
                        background: on ? `${meta.color}18` : "rgba(24,24,27,0.7)",
                        boxShadow: on ? `0 0 18px ${meta.color}40` : "none",
                      }}
                      data-testid={`toggle-hygiene-${t.key}`}
                    >
                      <span className="text-2xl">{t.emoji}</span>
                      <span className="flex-1 text-left text-sm font-semibold text-zinc-100">{t.label}</span>
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center transition-all"
                        style={{
                          background: on ? meta.color : "transparent",
                          border: on ? "none" : "2px solid #3f3f46",
                        }}
                      >
                        {on && <Check className="w-4 h-4 text-black" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Manual fields — kept but discreet */}
            {fields.length > 0 && (
              <div className={`space-y-3 ${ring === "nutrition" || ring === "hygiene" ? "mt-4 pt-4 border-t border-zinc-800/60" : "mt-4"}`}>
                {ring === "nutrition" && (
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">Manual (opcional)</p>
                )}
                {ring === "hygiene" && (
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">Estado geral</p>
                )}
                <div className={ring === "biomarkers" ? "grid grid-cols-2 gap-3" : "space-y-3"}>
                  {fields.map((f) => (
                    <div key={f.key}>
                      <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 block">{f.label}</label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder={f.placeholder}
                        value={values[f.key] ?? ""}
                        onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                        className="bg-zinc-900 border-zinc-800 text-zinc-100 h-11 text-base"
                        data-testid={`log-input-${ring}-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              onClick={handleSubmit}
              className="w-full mt-5 h-12 text-base font-bold text-black"
              style={{
                background: `linear-gradient(135deg, ${meta.color}, ${meta.color}dd)`,
                boxShadow: `0 8px 28px ${meta.color}55`,
              }}
              data-testid={`log-submit-${ring}`}
            >
              Registar {meta.label}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function PhaseExecute() {
  const navigate = useNavigate();
  const { program = "keto_fusion" } = useParams();
  const [state, setState] = useState(null);
  const [macros, setMacros] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logRing, setLogRing] = useState(null);
  const [attempting, setAttempting] = useState(false);
  const [prescriptionOpen, setPrescriptionOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [closeDayOpen, setCloseDayOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [phaseRes, macrosRes] = await Promise.all([
        fetch(`${API}/api/programs/${program}/phase`, { headers: authHeaders() }),
        (program === "keto_fusion" || program === "desafio_8k")
          ? fetch(`${API}/api/programs/${program}/macros/today`, { headers: authHeaders() })
          : Promise.resolve(null),
      ]);
      const data = await phaseRes.json();
      setState(data);
      if (macrosRes) {
        const m = await macrosRes.json();
        setMacros(m);
      }
    } catch {
      toast.error("Erro ao carregar fase");
    } finally {
      setLoading(false);
    }
  }, [program]);

  useEffect(() => { load(); }, [load]);

  const handleLog = async (ring, payload) => {
    try {
      await fetch(`${API}/api/programs/${program}/log/${ring}`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify(payload),
      });
      toast.success(`${RING_LABEL_PT[ring]} registado ✓`);
      setLogRing(null);
      load();
    } catch {
      toast.error("Falhou registar — tenta de novo");
    }
  };

  // Map first-in-missing back to a ring key + action (open modal / scroll)
  const RING_FROM_MISSING = [
    { prefix: "Nutrição", key: "nutrition", label: "Registar nutrição" },
    { prefix: "Treino", key: "training", label: "Registar treino" },
    { prefix: "Higiene", key: "hygiene", label: "Registar higiene" },
    { prefix: "Biomarcadores", key: "biomarkers", label: "Registar biomarcador" },
  ];

  const openRingLogger = (ringKey) => {
    if (ringKey === "training") setSessionOpen(true);
    else setLogRing(ringKey);
  };

  const handleAttemptGate = async () => {
    setAttempting(true);
    try {
      const res = await fetch(`${API}/api/programs/${program}/attempt-gate`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ phase: state.current_phase.id }),
      });
      const data = await res.json();
      if (data?.passed) {
        toast.success("🎉 Fase desbloqueada! Avanças para o próximo bloco.", {
          duration: 5000,
        });
      } else {
        const missing = Array.isArray(data?.missing) ? data.missing : [];
        // Find first ring that's missing to offer as CTA
        const firstMissing = RING_FROM_MISSING.find(
          (r) => missing.some((m) => m.startsWith(r.prefix))
        );
        toast.error(`Faltam ${missing.length} critério${missing.length === 1 ? "" : "s"}`, {
          description: missing.length
            ? missing.map((m) => `• ${m}`).join("\n")
            : "Regista os anéis desta fase para avançares.",
          duration: 8000,
          action: firstMissing
            ? {
                label: firstMissing.label,
                onClick: () => {
                  openRingLogger(firstMissing.key);
                  // scroll to phase gate panel for context
                  document
                    .querySelector('[data-testid="phase-gate-panel"]')
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                },
              }
            : undefined,
        });
        // Highlight the gate panel visually
        const panel = document.querySelector('[data-testid="phase-gate-panel"]');
        if (panel) {
          panel.scrollIntoView({ behavior: "smooth", block: "center" });
          panel.classList.add("ring-2", "ring-amber-400/70");
          setTimeout(() => panel.classList.remove("ring-2", "ring-amber-400/70"), 2500);
        }
      }
      load();
    } catch {
      toast.error("Erro ao avaliar gate");
    } finally {
      setAttempting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}>
          <Trophy className="w-10 h-10 text-lime-400" />
        </motion.div>
      </div>
    );
  }

  if (!state?.current_phase) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-zinc-400">
        Programa não disponível.
      </div>
    );
  }

  const phase = state.current_phase;
  const rings = state.rings;
  const gate = state.gate;
  const gatePassed = gate.passed;

  return (
    <div className="min-h-screen bg-black text-zinc-100 relative overflow-x-hidden">
      {/* Ambient cinematic aurora — elite background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full"
          style={{
            background: `radial-gradient(circle, ${phase.color}40, transparent 60%)`,
            filter: "blur(140px)",
          }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full"
          style={{
            background: `radial-gradient(circle, ${phase.color}30, transparent 65%)`,
            filter: "blur(120px)",
          }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        {/* Grid overlay for depth */}
        <div className="absolute inset-0" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }} />
      </div>

      <div className="max-w-5xl mx-auto px-5 py-6 relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/keto-fusion")} className="text-zinc-400 hover:text-zinc-100 flex items-center gap-1 text-sm" data-testid="phase-back">
            <ChevronLeft className="w-4 h-4" /> {state.program_name_pt}
          </button>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
            Bloco {phase.id} · {phase.code}
          </span>
        </div>

        {/* Hero — cinematic */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8 relative">
          <motion.div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
            style={{ background: `${phase.color}15`, border: `1px solid ${phase.color}40` }}
            animate={{ boxShadow: [`0 0 10px ${phase.color}30`, `0 0 25px ${phase.color}60`, `0 0 10px ${phase.color}30`] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: phase.color }} />
            <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: phase.color }}>
              Fase Activa · {phase.duration_days} dias
            </span>
          </motion.div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-2"
            style={{
              background: `linear-gradient(135deg, ${phase.color}, #ffffff 40%, ${phase.color})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: `0 0 40px ${phase.color}30`,
            }}
          >
            {phase.name_pt}
          </h1>
          <p className="text-sm italic text-zinc-400 max-w-lg mx-auto">{phase.tagline_pt}</p>
        </motion.div>

        <SpiritWeekGuideCard program={program} onWeekClosed={() => load()} />

        {/* Rings + Gate */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Left — Rings */}
          <div className="flex flex-col items-center p-6 rounded-2xl bg-zinc-950/50 border border-zinc-800">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-4">Anéis ao vivo desta fase</p>
            <RingsLive rings={rings} size={240} onRingClick={(ringKey) => {
              if (ringKey === "training") setSessionOpen(true);
              else setLogRing(ringKey);
            }} />

            {/* Fechar Dia — one-tap flow, primary CTA */}
            <Button
              onClick={() => setCloseDayOpen(true)}
              className="w-full mt-5 h-12 text-base font-bold text-black"
              style={{
                background: "linear-gradient(135deg, #84cc16 0%, #10b981 100%)",
                boxShadow: "0 8px 24px rgba(132,204,22,0.45), inset 0 1px 0 rgba(255,255,255,0.15)",
              }}
              data-testid="close-day-btn"
            >
              🎯 Fechar Dia · 30 seg
            </Button>
            <p className="text-[11px] text-zinc-500 mt-3 text-center">
              Ou toca num tile · <span className="text-emerald-400">Verde = feito</span>
            </p>
          </div>

          {/* Right — Gate criteria */}
          <div className="p-6 rounded-2xl bg-zinc-950/50 border border-zinc-800" data-testid="phase-gate-panel">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Phase Gate</p>
              {gatePassed ? (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-400">
                  <Check className="w-3 h-3" /> Pronto
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
                  <Lock className="w-3 h-3" /> Em progresso
                </span>
              )}
            </div>

            <h3 className="text-lg font-bold text-zinc-100 mb-3">
              {gatePassed ? "Pronto para avançar." : "Critérios para passar:"}
            </h3>

            <ul className="space-y-2 mb-5">
              {Object.entries({
                Nutrição: `${rings.nutrition.pct}% / ${rings.nutrition.target_pct}%`,
                Treino: `${rings.training.sessions_done} / ${rings.training.sessions_target} sessões`,
                Higiene: `${rings.hygiene.pct}% / ${rings.hygiene.target_pct}%`,
                Biomarcadores: `${rings.biomarkers.entries_done} / ${rings.biomarkers.entries_target} registos`,
              }).map(([k, v]) => {
                const isOk = gate.missing.every((m) => !m.startsWith(k));
                return (
                  <li key={k} className="flex items-center gap-2 text-sm">
                    {isOk ? (
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    ) : (
                      <span className="w-4 h-4 rounded-full border-2 border-zinc-700 flex-shrink-0" />
                    )}
                    <span className="text-zinc-300">{k}:</span>
                    <span className={isOk ? "text-emerald-400 font-mono text-xs" : "text-zinc-400 font-mono text-xs"}>{v}</span>
                  </li>
                );
              })}
            </ul>

            <Button
              onClick={handleAttemptGate}
              disabled={attempting}
              className="w-full bg-gradient-to-r from-lime-500 to-emerald-500 text-black font-bold disabled:opacity-50"
              data-testid="phase-attempt-gate"
            >
              {attempting ? "A avaliar..." : gatePassed ? "🔓 Desbloquear próxima fase" : "Verificar progresso"}
            </Button>
          </div>
        </div>

        {/* Spirit adaptive nudges — appear above macros when relevant */}
        {program === "keto_fusion" && <SpiritNudgeCard program={program} />}

        {/* Daily Focus — ONE mission for today (Spirit-picked, gentle) */}
        {program === "keto_fusion" && <DailyFocusCard program={program} phase={phase} />}

        {/* Macro Board — enabled for keto_fusion (with all extras) AND desafio_8k (simplified) */}
        {(program === "keto_fusion" || program === "desafio_8k") && macros?.target && (
          <div className="mb-6">
            {/* Refeed Window Timer — ONLY Fusion Blocos 2/3 */}
            {program === "keto_fusion" && (
              <RefeedTimer
                lastTrainingAt={state.roadmap && (
                  macros.consumed?.last_training_at || null
                )}
                phaseId={phase.id}
                onOpenLog={() => setLogRing("nutrition")}
              />
            )}

            <FusionMacroBoard
              target={macros.target}
              consumed={macros.consumed}
              phase={phase}
            />

            {/* CTAs — both programs now support Spirit meal prescription */}
            <div className="grid grid-cols-3 gap-3 mt-3">
              <button
                onClick={() => navigate("/ketoscan")}
                className="p-3 rounded-xl border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-xs font-bold flex items-center justify-center gap-1 transition-all"
                data-testid="macro-scan-btn"
              >
                📸 KetoScan
              </button>
              <button
                onClick={() => setPrescriptionOpen(true)}
                className="p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-xs font-bold flex items-center justify-center gap-1 transition-all"
                data-testid="macro-rx-btn"
              >
                ✨ Spirit sugere
              </button>
              <button
                onClick={() => setWizardOpen(true)}
                className="p-3 rounded-xl border border-lime-500/30 bg-lime-500/10 hover:bg-lime-500/20 text-lime-300 text-xs font-bold flex items-center justify-center gap-1 transition-all"
                data-testid="macro-wizard-btn"
              >
                🎯 Objectivo
              </button>
            </div>
          </div>
        )}

        {/* Weekly cycling calendar — ONLY Fusion (8K não tem cycling) */}
        {program === "keto_fusion" && (
          <div className="mb-10 space-y-4">
            <WeeklyLessonCard program={program} />
            <RoadmapPreviewStrip program={program} />
            <TdeeCalibrationBadge program={program} />
            <TrainingScheduleSelector program={program} onSaved={load} />
            <WeeklyMacroCalendar program={program} />
          </div>
        )}

        {program === "desafio_8k" && (
          <div className="mb-10 space-y-4">
            <WeeklyLessonCard program={program} />
            <RoadmapPreviewStrip program={program} />
          </div>
        )}

        {/* Roadmap */}
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3 text-center">Tua jornada {state.program_name_pt}</p>
        <PhaseRoadmap phases={state.roadmap} onPhaseClick={() => {}} />
      </div>

      <LogModal
        open={!!logRing}
        ring={logRing}
        onClose={() => setLogRing(null)}
        onSubmit={handleLog}
      />

      <MealPrescriptionModal
        open={prescriptionOpen}
        program={program}
        onClose={() => setPrescriptionOpen(false)}
        onSelect={async (meal) => {
          const cur = macros?.consumed || {};
          await handleLog("nutrition", {
            carbs_g: (cur.carbs_g || 0) + (meal.carbs_g || 0),
            protein_g: (cur.protein_g || 0) + (meal.protein_g || 0),
            fat_g: (cur.fat_g || 0) + (meal.fat_g || 0),
            kcal: (cur.kcal || 0) + (meal.kcal || 0),
          });
          setPrescriptionOpen(false);
        }}
      />

      <FusionGoalWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSaved={load}
      />

      <SessionLoggerModal
        open={sessionOpen}
        program={program}
        onClose={() => setSessionOpen(false)}
        onSaved={load}
      />

      <CloseDayModal
        open={closeDayOpen}
        program={program}
        biomarkerLog={
          state?.last_log_dates?.biomarkers
            ? [{ date: state.last_log_dates.biomarkers }]
            : []
        }
        onClose={() => setCloseDayOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
